import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Calculator } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Ersteinschaetzung = () => {
  const [formData, setFormData] = useState({
    // Kontaktdaten
    name: '',
    email: '',
    telefon: '',
    
    // Objektdaten
    objekttyp: '',
    standort_plz: '',
    standort_stadt: '',
    verkaufsflaeche: '',
    grundstuecksgroesse: '',
    
    // Mieterdaten
    hauptmieter: '',
    jahresmiete: '',
    restlaufzeit: '',
    optionen_vorhanden: '',
    
    // Zusätzlich
    verkaufsgrund: '',
    zeitrahmen: '',
    nachricht: '',
    
    // Consent
    consent_contact: false,
    consent_marketing: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consent_contact) {
      toast.error('Bitte bestätigen Sie, dass wir Sie kontaktieren dürfen.');
      return;
    }

    if (!formData.name || !formData.email || !formData.objekttyp) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    setLoading(true);
    try {
      // Send lead with detailed object info in nachricht field
      const detailsText = `
Objekttyp: ${formData.objekttyp}
Standort: ${formData.standort_plz} ${formData.standort_stadt}
Verkaufsfläche: ${formData.verkaufsflaeche || 'k.A.'} m²
Grundstücksgröße: ${formData.grundstuecksgroesse || 'k.A.'} m²
Hauptmieter: ${formData.hauptmieter || 'k.A.'}
Jahresmiete: ${formData.jahresmiete || 'k.A.'} €
Restlaufzeit: ${formData.restlaufzeit || 'k.A.'}
Optionen: ${formData.optionen_vorhanden || 'k.A.'}
Verkaufsgrund: ${formData.verkaufsgrund || 'k.A.'}
Zeitrahmen: ${formData.zeitrahmen || 'k.A.'}
Nachricht: ${formData.nachricht || 'k.A.'}
      `.trim();

      const leadResponse = await axios.post(`${API}/leads`, {
        rolle: 'Eigentümer',
        name: formData.name,
        email: formData.email,
        telefon: formData.telefon || null,
        quelle: 'Kostenlose Ersteinschätzung',
        consent_contact: formData.consent_contact,
        consent_marketing: formData.consent_marketing,
        nachricht: detailsText
      });
      
      // If marketing consent given, trigger DOI flow
      if (formData.consent_marketing) {
        try {
          await axios.post(`${API}/doi/send`, {
            email: formData.email,
            lead_id: leadResponse.data.lead_id || null
          });
          toast.success('Vielen Dank! Bitte bestätigen Sie Ihre E-Mail-Adresse, um Zugang zu unseren kostenlosen Guides und Checklisten zu erhalten.', {
            duration: 6000
          });
        } catch (doiError) {
          console.error('DOI send error:', doiError);
          // Don't show error to user, just log it - lead was captured successfully
          toast.success('Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen mit einer ersten Einschätzung.');
        }
      } else {
        toast.success('Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen mit einer ersten Einschätzung.');
      }
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        telefon: '',
        objekttyp: '',
        standort_plz: '',
        standort_stadt: '',
        verkaufsflaeche: '',
        grundstuecksgroesse: '',
        hauptmieter: '',
        jahresmiete: '',
        restlaufzeit: '',
        optionen_vorhanden: '',
        verkaufsgrund: '',
        zeitrahmen: '',
        nachricht: '',
        consent_contact: false,
        consent_marketing: false,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Senden des Formulars');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Kostenlose Ersteinschätzung – Handelsfläche bewerten lassen"
        description="Erhalten Sie eine kostenlose Ersteinschätzung für Ihre Handelsimmobilie. Detaillierte Objekterfassung für präzise Bewertung. Antwort innerhalb von 24 Stunden."
        url="/ersteinschaetzung"
      />
      <Navbar />
      <main className="flex-grow bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4A853] to-[#F0C870] mb-6 mx-auto"></div>
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: 'var(--navy-900)' }}
            >
              Kostenlose Ersteinschätzung
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--gray-600)' }}>
              Erhalten Sie innerhalb von 24 Stunden eine erste Einschätzung des Verkaufswerts 
              Ihrer Handelsimmobilie – unverbindlich und kostenlos.
            </p>
          </div>

          <Card className="glass-card p-8">
            <form onSubmit={handleSubmit} data-testid="ersteinschaetzung-form">
              {/* Kontaktdaten */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--navy-800)' }}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: 'var(--gold-600)' }}>1</span>
                  Ihre Kontaktdaten
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Max Mustermann"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">
                      E-Mail <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="max@example.com"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="telefon">Telefon (optional für Rückfragen)</Label>
                    <Input
                      id="telefon"
                      value={formData.telefon}
                      onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                      placeholder="+49 123 456789"
                    />
                  </div>
                </div>
              </div>

              {/* Objektdaten */}
              <div className="mb-8 pt-8 border-t border-gray-200">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--navy-800)' }}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: 'var(--gold-600)' }}>2</span>
                  Objektinformationen
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="objekttyp">
                      Objekttyp <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.objekttyp} onValueChange={(value) => setFormData({ ...formData, objekttyp: value })}>
                      <SelectTrigger id="objekttyp">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Lebensmittelmarkt">Lebensmittelmarkt (Edeka, Rewe, Kaufland)</SelectItem>
                        <SelectItem value="Discounter">Discounter (Aldi, Lidl, Netto, Penny)</SelectItem>
                        <SelectItem value="Drogerie">Drogeriemarkt (dm, Rossmann, Müller)</SelectItem>
                        <SelectItem value="Baumarkt">Baumarkt (Obi, Hornbach, Bauhaus, Toom)</SelectItem>
                        <SelectItem value="Fachmarkt">Fachmarkt (MediaMarkt, Decathlon, andere)</SelectItem>
                        <SelectItem value="Fachmarktzentrum">Fachmarktzentrum / Retail Park</SelectItem>
                        <SelectItem value="Großfläche">Großfläche (SB-Warenhaus, Cash & Carry)</SelectItem>
                        <SelectItem value="Sonstiges">Sonstiges Handelsformat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="standort_plz">PLZ</Label>
                    <Input
                      id="standort_plz"
                      value={formData.standort_plz}
                      onChange={(e) => setFormData({ ...formData, standort_plz: e.target.value })}
                      placeholder="12345"
                      maxLength={5}
                    />
                  </div>

                  <div>
                    <Label htmlFor="standort_stadt">Stadt/Ort</Label>
                    <Input
                      id="standort_stadt"
                      value={formData.standort_stadt}
                      onChange={(e) => setFormData({ ...formData, standort_stadt: e.target.value })}
                      placeholder="Berlin"
                    />
                  </div>

                  <div>
                    <Label htmlFor="verkaufsflaeche">Verkaufsfläche (m²)</Label>
                    <Input
                      id="verkaufsflaeche"
                      type="number"
                      value={formData.verkaufsflaeche}
                      onChange={(e) => setFormData({ ...formData, verkaufsflaeche: e.target.value })}
                      placeholder="z.B. 1200"
                    />
                  </div>

                  <div>
                    <Label htmlFor="grundstuecksgroesse">Grundstücksgröße (m²)</Label>
                    <Input
                      id="grundstuecksgroesse"
                      type="number"
                      value={formData.grundstuecksgroesse}
                      onChange={(e) => setFormData({ ...formData, grundstuecksgroesse: e.target.value })}
                      placeholder="z.B. 5000"
                    />
                  </div>
                </div>
              </div>

              {/* Mieterdaten */}
              <div className="mb-8 pt-8 border-t border-gray-200">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--navy-800)' }}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: 'var(--gold-600)' }}>3</span>
                  Mieter & Mietvertrag
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="hauptmieter">Hauptmieter / Betreiber</Label>
                    <Input
                      id="hauptmieter"
                      value={formData.hauptmieter}
                      onChange={(e) => setFormData({ ...formData, hauptmieter: e.target.value })}
                      placeholder="z.B. Edeka, Aldi, Obi"
                    />
                  </div>

                  <div>
                    <Label htmlFor="jahresmiete">Jahresmiete (€)</Label>
                    <Input
                      id="jahresmiete"
                      type="number"
                      value={formData.jahresmiete}
                      onChange={(e) => setFormData({ ...formData, jahresmiete: e.target.value })}
                      placeholder="z.B. 100000"
                    />
                  </div>

                  <div>
                    <Label htmlFor="restlaufzeit">Restlaufzeit Mietvertrag</Label>
                    <Select value={formData.restlaufzeit} onValueChange={(value) => setFormData({ ...formData, restlaufzeit: value })}>
                      <SelectTrigger id="restlaufzeit">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unter_2">Unter 2 Jahre</SelectItem>
                        <SelectItem value="2_5">2-5 Jahre</SelectItem>
                        <SelectItem value="5_8">5-8 Jahre</SelectItem>
                        <SelectItem value="8_12">8-12 Jahre</SelectItem>
                        <SelectItem value="ueber_12">Über 12 Jahre</SelectItem>
                        <SelectItem value="weiss_nicht">Weiß ich nicht</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="optionen">Verlängerungsoptionen vorhanden?</Label>
                    <Select value={formData.optionen_vorhanden} onValueChange={(value) => setFormData({ ...formData, optionen_vorhanden: value })}>
                      <SelectTrigger id="optionen">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nein">Nein</SelectItem>
                        <SelectItem value="weiss_nicht">Weiß ich nicht</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Verkaufssituation */}
              <div className="mb-8 pt-8 border-t border-gray-200">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2" style={{ color: 'var(--navy-800)' }}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: 'var(--gold-600)' }}>4</span>
                  Verkaufssituation
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="verkaufsgrund">Verkaufsgrund (optional)</Label>
                    <Select value={formData.verkaufsgrund} onValueChange={(value) => setFormData({ ...formData, verkaufsgrund: value })}>
                      <SelectTrigger id="verkaufsgrund">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portfolio_optimierung">Portfolio-Optimierung</SelectItem>
                        <SelectItem value="generationswechsel">Generationswechsel</SelectItem>
                        <SelectItem value="liquiditaet">Liquiditätsbedarf</SelectItem>
                        <SelectItem value="anderes_investment">Anderes Investment geplant</SelectItem>
                        <SelectItem value="sonstiges">Sonstiges</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="zeitrahmen">Gewünschter Zeitrahmen</Label>
                    <Select value={formData.zeitrahmen} onValueChange={(value) => setFormData({ ...formData, zeitrahmen: value })}>
                      <SelectTrigger id="zeitrahmen">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sofort">Innerhalb 3 Monate</SelectItem>
                        <SelectItem value="6_monate">Innerhalb 6 Monate</SelectItem>
                        <SelectItem value="12_monate">Innerhalb 12 Monate</SelectItem>
                        <SelectItem value="flexibel">Flexibel / keine Eile</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="nachricht">Zusätzliche Informationen (optional)</Label>
                    <Textarea
                      id="nachricht"
                      value={formData.nachricht}
                      onChange={(e) => setFormData({ ...formData, nachricht: e.target.value })}
                      placeholder="Besonderheiten des Objekts, spezielle Fragen, etc."
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              {/* Consent */}
              <div className="space-y-4 pt-6 border-t border-gray-200 mb-8">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent_contact"
                    checked={formData.consent_contact}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent_contact: checked })}
                  />
                  <Label
                    htmlFor="consent_contact"
                    className="text-sm cursor-pointer"
                    style={{ color: 'var(--gray-700)' }}
                  >
                    Ich willige ein, dass RetailInvest mich per E-Mail oder Telefon kontaktieren
                    darf, um eine Ersteinschätzung zu erstellen. <span className="text-red-500">*</span>
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent_marketing"
                    checked={formData.consent_marketing}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent_marketing: checked })}
                  />
                  <Label
                    htmlFor="consent_marketing"
                    className="text-sm cursor-pointer"
                    style={{ color: 'var(--gray-700)' }}
                  >
                    Ich möchte zukünftig über Marktanalysen und Insights informiert werden (optional).
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-gold-gradient text-white border-0 font-semibold py-7 text-lg"
              >
                <Calculator className="mr-2 h-5 w-5" />
                {loading ? 'Wird gesendet...' : 'Kostenlose Ersteinschätzung anfordern'}
              </Button>

              <p className="text-xs text-center mt-4" style={{ color: 'var(--gray-500)' }}>
                Ihre Daten werden vertraulich behandelt. Weitere Informationen in unserer{' '}
                <a href="/datenschutz" className="underline" style={{ color: 'var(--navy-600)' }}>
                  Datenschutzerklärung
                </a>.
              </p>
            </form>
          </Card>

          {/* Info Box */}
          <Card className="glass-card p-6 mt-8 bg-blue-50">
            <h3 className="font-bold mb-3" style={{ color: 'var(--navy-800)' }}>
              Was passiert nach dem Absenden?
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--gray-700)' }}>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--gold-600)' }}>1.</span>
                <span>Wir prüfen Ihre Angaben und recherchieren vergleichbare Objekte</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--gold-600)' }}>2.</span>
                <span>Sie erhalten innerhalb von 24 Stunden eine erste Einschätzung per E-Mail</span>
              </li>
              <li className="flex items-start gap-2">
                <span style={{ color: 'var(--gold-600)' }}>3.</span>
                <span>Optional: Persönliches Gespräch zur detaillierten Bewertung und Verkaufsstrategie</span>
              </li>
            </ul>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Ersteinschaetzung;
